import type { ReactNode } from 'react';
import { AuthField, AuthFrame, TextInput, cn } from '@forecastify/ui';
import logo from '@forecastify/ui/assets/forecastify-logo-white.png';
import { LOCALES, useI18n, useT } from '../i18n';
import { AuthBackdrop } from './auth-backdrop';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The frame around sign-in, enrolment, joining and signed-out.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE PRODUCT'S SIGN-IN PAGE, ON PURPOSE, WITH FOUR DIFFERENCES** (asked for on 8 Oct 2026,
 * reversing the earlier 400px sidebar-tone column): `packages/ui`'s `AuthFrame` with the white
 * wordmark and the animated swell, and —
 *
 * 1. a *CONTROL PANEL* box under the wordmark, so nobody mistakes which door this is;
 * 2. the swell in the Forecastify green rather than the product's blue and violet;
 * 3. no passkey, Google or sign-up — an operator has none of the three;
 * 4. a page footer with the company and the language switch, and not the product's
 *    *Adatkezelés* and *Súgó*.
 *
 * ⚠️ **The language switch stays**, because the person who needs English most is the one who
 * cannot read the Hungarian refusal — and there is no signed-in chrome to hold one yet.
 */
export function AuthCard({
  screen,
  title,
  children,
}: {
  /** The route's `data-screen` marker — the route suite finds each screen by it. */
  readonly screen: string;
  /** The card's heading, centred, as the product draws it. */
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  const t = useT();
  const { locale, setLocale } = useI18n();

  return (
    <AuthFrame
      screen={screen}
      title={title}
      logo={{ src: logo, alt: t('app.name') }}
      badge={
        <span className="bg-foreground text-background rounded-[5px] px-2 py-0.5 text-[10.5px] leading-4 font-semibold tracking-[.14em]">
          {t('auth.label')}
        </span>
      }
      backdrop={<AuthBackdrop />}
      pageFooter={
        /* ⚠️ The product's footer type, on the most saturated band of the swell. Its contrast
           note in `apps/web/src/screens/auth/auth-frame.tsx` holds here too. */
        <div className="text-primary-foreground absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3.5 p-6 text-[12.5px]">
          <span>{t('auth.footer.company')}</span>
          {setLocale === undefined ? null : (
            <div role="group" aria-label={t('nav.language')} className="flex gap-0.5">
              {LOCALES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={option === locale}
                  onClick={() => {
                    setLocale(option);
                  }}
                  className={cn(
                    'h-6 rounded px-1.5 text-[11.5px] font-medium',
                    option === locale
                      ? 'bg-background text-foreground'
                      : 'opacity-80 hover:opacity-100',
                  )}
                >
                  {option.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      }
    >
      {children}
    </AuthFrame>
  );
}

/** A line of explanation under the card's title — the product's muted 12.5px. */
export function AuthLede({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <p className="text-muted-foreground text-center text-[12.5px] leading-[1.5] text-pretty">
      {children}
    </p>
  );
}

/**
 * A labelled field — the product's `AuthField`, plus the error line the console's forms carry.
 *
 * ⚠️ `AuthField` gives the control its id, so the label is wired without the caller naming one.
 */
export function AuthInput({
  label,
  error,
  children,
}: {
  readonly label: string;
  readonly error?: string | null;
  readonly children: Parameters<typeof AuthField>[0]['children'];
}): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <AuthField label={label}>{children}</AuthField>
      {error === undefined || error === null ? null : (
        <span role="alert" className="text-destructive text-[12px]">
          {error}
        </span>
      )}
    </div>
  );
}

/** The six-digit field, as the product's code step draws it. */
export const CODE_INPUT = 'text-center tracking-[0.4em] tabular-nums';

export { TextInput };

/**
 * The step bar the mockup draws on its enrolment card — a segment per step, filled up to the
 * current one, with its name under it.
 *
 * ⚠️ Shared by `/enrol` and `/join`, so the two flows that end in the same factor read as one.
 */
export function AuthSteps({
  label,
  steps,
  current,
}: {
  readonly label: string;
  readonly steps: readonly string[];
  readonly current: number;
}): ReactNode {
  return (
    <ol
      aria-label={label}
      className="grid gap-1.5"
      style={{ gridTemplateColumns: `repeat(${String(steps.length)}, minmax(0, 1fr))` }}
    >
      {steps.map((name, index) => (
        <li
          key={name}
          aria-current={index === current ? 'step' : undefined}
          className="flex flex-col gap-1.5"
        >
          <span className={cn('h-1 rounded-full', index <= current ? 'bg-primary' : 'bg-muted')} />
          <span
            className={cn(
              'text-[11.5px]',
              index === current ? 'font-medium' : 'text-muted-foreground',
            )}
          >
            {name}
          </span>
        </li>
      ))}
    </ol>
  );
}
